import { useGesture } from '@use-gesture/react';
import { clsx } from 'clsx';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  memo,
  type PropsWithChildren,
  useRef,
  useState,
} from 'react';

import * as styles from './Overlay.sty.js';
import { useFocusRing } from '../../../core/styles/useFocusRing.js';
import { clamp } from '../../split-layout/width-utils.js';

export const OVERLAY_HANDLE_WIDTH = 8;

/** @internal */
export interface OverlayProps
  extends PropsWithChildren,
    ComponentPropsWithoutRef<'div'> {
  resizable?: boolean;
  onResize?: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
}

/** @internal */
export const Overlay = memo(
  forwardRef<HTMLDivElement, OverlayProps>((props, forwardedRef) => {
    const {
      children,
      className,
      style,
      minWidth = 200,
      maxWidth = Infinity,
      defaultWidth = 300,
      resizable = true,
      onResize,
      ...restProps
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLHRElement>(null);

    const { focusProps, focusClassName } = useFocusRing({
      focusWithin: true,
      ignoreModality: true,
    });

    const dragStartWidth = useRef<number | null>(null);
    const clampContentWidth = (width: number) =>
      clamp(minWidth, width, maxWidth);
    const clampedDefaultWidth = clampContentWidth(defaultWidth);
    const [resizedWidth, setResizedWidth] = useState<number | null>(null);

    const contentWidth =
      resizedWidth === null
        ? clampedDefaultWidth
        : clampContentWidth(resizedWidth);

    const overlayWidth = resizable
      ? contentWidth + OVERLAY_HANDLE_WIDTH
      : contentWidth;

    useGesture(
      {
        onDragStart: () => {
          dragStartWidth.current = clampContentWidth(contentWidth);
        },
        onDragEnd: () => {
          dragStartWidth.current = null;
        },
        onDrag: (state) => {
          if (dragStartWidth.current === null) {
            return;
          }

          const nextWidth = clamp(
            minWidth,
            dragStartWidth.current + state.movement[0] * -1,
            maxWidth,
          );
          setResizedWidth(nextWidth);
          onResize?.(nextWidth);
        },
      },
      { target: handleRef },
    );

    return (
      <div
        role="dialog"
        className={styles.overlay}
        style={{ ...style, width: overlayWidth }}
        ref={containerRef}
      >
        {resizable ? (
          <div className={styles.handleContainer}>
            <hr
              {...focusProps}
              ref={handleRef}
              className={clsx(styles.resizeHandle, focusClassName)}
              tabIndex={0}
            />
          </div>
        ) : null}
        <div
          {...restProps}
          ref={forwardedRef}
          className={clsx(styles.overlayContent, className)}
        >
          {children}
        </div>
      </div>
    );
  }),
);
Overlay.displayName = 'Overlay';
